/** Guild shop input/receipts. Only a server acknowledgment can settle a purchase. */
import { S } from '../core/state.js';
import { send, townActionAllowed } from '../core/net.js';
import { guildInteger } from './guild-money.js';

const STORAGE = 'unige.guild.shop.receipt.v1';
let replyTimer = 0;
const busy = () => !!(S.guildShopPending || S.guildMoneyPending);
const connected = () => S.connected && S.ws?.readyState === 1;
function save() {
  try {
    if (S.guildShopPending) sessionStorage.setItem(STORAGE, JSON.stringify(S.guildShopPending));
    else sessionStorage.removeItem(STORAGE);
  } catch { /* Current-tab receipt still survives in memory when browser storage is disabled. */ }
}

export function restoreGuildShop() {
  if (S.guildShopPending) return;
  try {
    const p = JSON.parse(sessionStorage.getItem(STORAGE) || 'null'), r = p?.request;
    if (p?.charId !== S.charId || !r || !/^[a-f0-9-]{36}$/i.test(r.receipt)) return;
    const purchase = r.t === 'guildShopBuy' && r.guildId && r.productId && guildInteger(r.quotedHonor) > 0n
      && (r.quantity === undefined || (Number.isInteger(r.quantity) && r.quantity >= 1));
    const moving = r.t === 'guildItemMove' && typeof r.itemId === 'string' && ['in', 'out'].includes(r.direction);
    if (!purchase && !moving) return;
    S.guildShopPending = { ...p, status: 'uncertain' };
  } catch { /* Corrupt storage is never turned into a purchase request. */ }
}

export function guildShopChoose(productId, redraw) {
  const shop = S.guild?.shop, product = shop?.products?.find((p) => p.id === productId);
  if (busy() || !product || !shop.enabled || !shop.canBuy || !S.guild?.me) return;
  if (!connected()) { S.guildShopNotice = '연결을 복구하는 중이다. 잠시 뒤 다시 시도해 달라.'; redraw(true); return; }
  if (guildInteger(shop.honor) < guildInteger(product.honor) || (product.kind === 'elixir' && shop.elixirBought)) return;
  S.guildShopConfirmation = { charId: S.charId, product: { ...product },
    request: { t: 'guildShopBuy', guildId: S.guild.me.guild.id, receipt: crypto.randomUUID(), productId, quotedHonor: String(product.honor), quantity: 1 } };
  S.guildShopNotice = ''; redraw(true);
}

/** UNI-Q132 — 이 상품을 한 번에 살 수 있는 최대 수량: 서버 상한(maxQuantity)과 명예로 살 수 있는 개수 중 작은 값(최소 1) */
export function guildShopQuantityCap(shop, product) {
  const max = BigInt(Math.max(1, Math.floor(Number(shop?.maxQuantity) || 1)));
  const price = guildInteger(product?.honor);
  if (price <= 0n) return 1;
  const affordable = guildInteger(shop?.honor) / price;
  return Number(affordable < 1n ? 1n : affordable < max ? affordable : max);
}
/** 확인 창의 수량을 바꾼다 — 선택은 구매가 아니다(확정 버튼만 보낸다) */
export function guildShopQuantity(value, redraw) {
  const draft = S.guildShopConfirmation;
  if (busy() || !draft || draft.charId !== S.charId) return;
  const cap = guildShopQuantityCap(S.guild?.shop, draft.product), n = Math.floor(Number(value));
  draft.request.quantity = Math.max(1, Math.min(cap, Number.isFinite(n) ? n : 1));
  redraw(true);
}

function transmit(redraw) {
  const pending = S.guildShopPending;
  if (!pending || pending.charId !== S.charId) return;
  if (!connected()) {
    pending.status = 'uncertain'; S.guildShopNotice = '연결을 확인한 뒤 처리 결과를 다시 확인해 달라. 구매·장비 이동은 중복 처리되지 않는다.'; save(); redraw(true); return;
  }
  pending.status = 'waiting'; save(); send(pending.request);
  clearTimeout(replyTimer);
  replyTimer = setTimeout(() => {
    if (S.guildShopPending?.request.receipt !== pending.request.receipt) return;
    pending.status = 'uncertain'; save(); redraw(true);
  }, 12000);
  redraw(true);
}

export function guildShopConfirm(redraw) {
  const draft = S.guildShopConfirmation, shop = S.guild?.shop;
  if (busy() || !draft || draft.charId !== S.charId || draft.request.guildId !== S.guild?.me?.guild?.id) return;
  const current = shop?.products?.find((p) => p.id === draft.request.productId), quantity = draft.request.quantity ?? 1;
  if (!shop?.enabled || !shop.canBuy || !current || String(current.honor) !== draft.request.quotedHonor
    || !Number.isInteger(quantity) || quantity < 1 || quantity > guildShopQuantityCap(shop, current)
    || guildInteger(shop.honor) < guildInteger(current.honor) || (current.kind === 'elixir' && shop.elixirBought)) {
    S.guildShopConfirmation = null; S.guildShopNotice = '가격·잔액·구매 조건이 바뀌었다. 상품을 다시 선택해 확인해 달라.'; redraw(true); return;
  }
  S.guildShopConfirmation = null;
  S.guildShopPending = { charId: S.charId, request: draft.request, name: quantity > 1 ? `${current.name} ×${quantity}` : current.name, status: 'waiting' };
  transmit(redraw);
}
export function guildShopCancel(redraw) { S.guildShopConfirmation = null; redraw(true); }
export function guildShopRetry(redraw) { transmit(redraw); }
/** Bound gear uses the same uncertain-receipt UI as buying; ordinary vault moves stay unchanged. */
export function guildItemMove(item, direction, redraw) {
  if (item?.guildBound == null || !['in', 'out'].includes(direction) || busy()) return;
  if (!connected()) { S.guildShopNotice = '연결을 복구하는 중이다. 잠시 뒤 다시 시도해 달라.'; redraw(true); return; }
  // 1.0 과 같게 마을 안전구역에서만(사용자 결정 2026-09-27). 사냥터에서 보내면 서버가 받지 않아
  // 「확인 중」이 풀리지 않으므로 대기 상태를 만들기 전에 이유를 보여준다.
  if (!townActionAllowed('guildItemMove')) { S.guildShopNotice = '길드 창고 이동은 마을 안전구역에서 할 수 있다.'; redraw(true); return; }
  S.guildShopConfirmation = null;
  S.guildShopPending = { charId: S.charId, name: '귀속 장비 ' + (direction === 'in' ? '맡기기' : '꺼내기'), status: 'waiting',
    request: { t: 'guildItemMove', receipt: crypto.randomUUID(), itemId: item.id, direction } };
  S.guildShopNotice = ''; transmit(redraw);
}
export function guildShopAck(message) {
  const pending = S.guildShopPending;
  if (!pending || pending.charId !== S.charId || pending.request.receipt !== message.receipt) return false;
  clearTimeout(replyTimer); replyTimer = 0;
  if (!message.ok && message.retryable) {
    pending.status = 'uncertain';
    S.guildShopNotice = message.error || '처리 결과를 확인 중이다. 결과 확인을 다시 눌러도 중복 처리되지 않는다.';
  } else {
    S.guildShopPending = null;
    S.guildShopNotice = message.ok ? (message.replayed ? '이미 완료된 요청이다. 중복 처리되지 않았다.'
      : pending.request.t === 'guildItemMove' ? '귀속 장비 이동 완료'
        : `구매 완료${(pending.request.quantity ?? 1) > 1 ? ` ×${pending.request.quantity}` : ''} · 내 최근 구매에서 확인 가능`)
      : message.error || '요청을 처리하지 못했다. 잔액·보유 장비·이용 조건을 확인해 달라.';
  }
  save(); return true;
}

export function guildShopFilter(key, value, redraw) {
  if (!['kind', 'grade', 'slot'].includes(key)) return;
  S.guildShopFilter[key] = String(value); redraw(true);
}
